import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";

import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject } from "../../utils/helpers.js";
import AlertDialog from "../dialogs/alertDialog.js";
import BotpressProductsDialog from "../dialogs/botpressProductsDialog.js";
import BotpressProductsTable from "../ui/botpressProductsTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const cleanProductRecord = productRecord => {
  if (productRecord == null) {
    return {};
  }

  const { computed, stale, ...cleanProduct } = productRecord;
  return cleanProduct;
};

const BotpressProducts = ({
  products,
  productPagination,
  productPageLoading,
  searchProductsRequest,
  updateProductRequest,
  deleteProductsRequest,
  widgetSize = "medium",
  minHeight = "333px",
}) => {
  const [rows, setRows] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDeleteProductIDs, setSelectedDeleteProductIDs] = useState(null);

  useEffect(() => {
    if (products != null) {
      setRows(products.map(productRecord => cleanProductRecord(productRecord)));
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const onRequestNewPage = async pagination => {
    try {
      searchProductsRequest(pagination);
    } catch (error) {
      console.error("Error getting product table rows", error);
      setRows([]);
    }
  };

  const onRefreshTable = () => {
    let newPagination = deepCopyObject(productPagination || {});
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };

  const onCreateProduct = () => {
    const newProduct = {
      id: null,
      type: "",
      archived: false,
      title: "",
      summary: "",
      startDate: null,
      endDate: null,
      cta: {},
      assets: {},
      metadata: null,
      matchSignals: [],
      softRequirements: [],
    };

    console.log("BOTPRESS PRODUCTS creating local product record", newProduct);
    setSelectedEntry(newProduct);
  };

  const onAcceptProduct = theRecord => {
    const isNewProduct = selectedEntry?.id == null;

    const productRecord = cleanProductRecord({
      ...theRecord,
      id: isNewProduct ? null : theRecord.id,
      matchSignals: Array.isArray(theRecord.matchSignals)
        ? theRecord.matchSignals
        : theRecord.matchSignals
          ? [theRecord.matchSignals]
          : [],
      softRequirements: Array.isArray(theRecord.softRequirements)
        ? theRecord.softRequirements
        : theRecord.softRequirements
          ? [theRecord.softRequirements]
          : [],
    });

    console.log("BOTPRESS PRODUCTS onAcceptProduct fired", {
      isNewProduct,
      productRecord,
    });

    updateProductRequest({ productRecord });
    setSelectedEntry(null);
  };

  const onDeleteProducts = productIDs => {
    deleteProductsRequest({ productIDs: productIDs });
    setSelectedDeleteProductIDs(null);
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle title={"Products"} text={rows != null ? `${rows.length} Products` : "Products"}>
        <OmniResponsiveButton
          icon={<Add />}
          tooltipTitle="Create a new product"
          buttonText="Product"
          onClick={onCreateProduct}
        />
      </WidgetTitle>

      {rows == null && <WidgetSkeleton />}

      {rows != null && (
        <Box
          sx={{
            opacity: productPageLoading ? 0.45 : 1,
            filter: productPageLoading ? "grayscale(80%)" : "none",
            pointerEvents: productPageLoading ? "none" : "auto",
            transition: "opacity 0.25s ease, filter 0.25s ease",
          }}
        >
          <WidgetSurveyContent>
            <BotpressProductsTable
              data={rows}
              onRequestNewPage={onRequestNewPage}
              dataReduxPagination={productPagination}
              onRefreshTable={onRefreshTable}
              onEditEntry={setSelectedEntry}
            />
          </WidgetSurveyContent>
        </Box>
      )}

      {selectedEntry != null && (
        <BotpressProductsDialog
          open={selectedEntry != null}
          productRecord={selectedEntry}
          dialogTitle={selectedEntry.id == null ? "Create Product" : "Edit Product"}
          okMessage={selectedEntry.id == null ? "Create" : "Ok"}
          onAccept={onAcceptProduct}
          onDelete={theRecord => {
            setSelectedEntry(null);

            if (theRecord.id != null) {
              setSelectedDeleteProductIDs([theRecord.id]);
            }
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedDeleteProductIDs != null && (
        <AlertDialog
          open={selectedDeleteProductIDs != null}
          dialogTitle={"Confirm"}
          dialogMessage={
            selectedDeleteProductIDs.length === 1
              ? "Are you sure you want to delete this Product?"
              : `Are you sure you want to delete these ${selectedDeleteProductIDs.length} Products?`
          }
          onAccept={() => {
            onDeleteProducts(selectedDeleteProductIDs);
          }}
          onClose={() => {
            setSelectedDeleteProductIDs(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  products: state.botpressSession.products,
  productPagination: state.botpressSession.productPagination,
  productPageLoading: state.botpressSession.productPageLoading,
});

const mapDispatchToProps = {
  searchProductsRequest: BotpressSessionActions.searchProductsRequest,
  updateProductRequest: BotpressSessionActions.updateProductRequest,
  deleteProductsRequest: BotpressSessionActions.deleteProductsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressProducts);
